import { Badge } from "@/client/components/ui/badge";
import type { ColumnDef, DatasetDescriptor } from "./console";

// Row types mirror the server read-models (console-read-models.ts). Each dataset is one descriptor.

export type SchoolStatus = "Niets" | "Aangemeld" | "Schoolgegevens" | "Programmaselectie" | "Inactief";

export type SchoolOverzichtRow = {
  schoolId: number;
  schoolName: string;
  officialName: string;
  shortName: string | null;
  brin: string | null;
  denomination: string | null;
  active: boolean;
  submitted: boolean;
  onboarded: boolean;
  status: SchoolStatus;
  statusRank: number;
  lastSubmittedAt: string | null;
  versionCount: number;
  groupCount: number | null;
  studentTotal: number | null;
  contactCount: number | null;
  coordinator: string | null;
  coordinatorEmail: string | null;
  coordinatorPhone: string | null;
  coordinatorCount: number | null;
  director: string | null;
  directorEmail: string | null;
  directorPhone: string | null;
  programmeTotal: number | null;
  vastCount: number | null;
  keuzeCount: number | null;
  technohubCount: number | null;
  staffTrainingCount: number | null;
  customCount: number | null;
  optOutCount: number | null;
  studiedagCount: number | null;
  address: string | null;
  schoolPhone: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  groupNames: string | null;
  selectedTitles: string | null;
  sectionOptOuts: string | null;
  year_groep_1: number | null;
  year_groep_2: number | null;
  year_groep_3: number | null;
  year_groep_4: number | null;
  year_groep_5: number | null;
  year_groep_6: number | null;
  year_groep_7: number | null;
  year_groep_8: number | null;
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
}
function csvDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}
const dash = (v: unknown) => (v == null ? "—" : String(v));

// compact number column helper (right-aligned, "—" for null in the cell, empty in CSV).
function num<Row>(id: string, label: string, group: string, accessor: (r: Row) => number | null, defaultVisible = false): ColumnDef<Row> {
  return {
    id, label, group, align: "right", defaultVisible,
    accessor,
    render: (r) => { const v = accessor(r); return v == null ? "—" : v; },
    csv: (r) => accessor(r) ?? "",
    sortValue: (r) => accessor(r),
  };
}
function text<Row>(id: string, label: string, group: string, accessor: (r: Row) => string | null, defaultVisible = false, mono = false): ColumnDef<Row> {
  return {
    id, label, group, defaultVisible, mono,
    accessor,
    render: (r) => dash(accessor(r)),
    csv: (r) => accessor(r) ?? "",
    sortValue: (r) => accessor(r) ?? "",
  };
}

const STATUS_VARIANT: Record<SchoolStatus, "success" | "outline" | "warning" | "info" | "secondary"> = {
  Niets: "warning",
  Aangemeld: "secondary",
  Schoolgegevens: "info",
  Programmaselectie: "success",
  Inactief: "outline",
};

const schoolsColumns: ColumnDef<SchoolOverzichtRow>[] = [
  {
    id: "schoolName", label: "School", group: "Basis", alwaysVisible: true, defaultVisible: true,
    accessor: (r) => r.schoolName,
    csv: (r) => r.schoolName,
    sortValue: (r) => r.schoolName,
  },
  text("brin", "Vestigingsnr.", "Basis", (r) => r.brin, true, true),
  {
    id: "status", label: "Status", group: "Basis", defaultVisible: true,
    accessor: (r) => r.status,
    render: (r) => <Badge variant={STATUS_VARIANT[r.status]} size="sm">{r.status}</Badge>,
    csv: (r) => r.status,
    sortValue: (r) => r.statusRank,
  },
  {
    id: "lastSubmittedAt", label: "Laatst ingediend", group: "Basis", defaultVisible: true,
    accessor: (r) => r.lastSubmittedAt,
    render: (r) => fmtDate(r.lastSubmittedAt),
    csv: (r) => csvDate(r.lastSubmittedAt),
    sortValue: (r) => r.lastSubmittedAt ?? "",
    filterValue: (r) => fmtDate(r.lastSubmittedAt),
  },
  num("versionCount", "Versies", "Basis", (r) => r.versionCount, true),
  num("groupCount", "Groepen", "Leerlingen", (r) => r.groupCount, true),
  num("studentTotal", "Leerlingen", "Leerlingen", (r) => r.studentTotal, true),
  text("coordinator", "Kunstcoördinator(en)", "Contact", (r) => r.coordinator, true),
  text("coordinatorEmail", "KC e-mail", "Contact", (r) => r.coordinatorEmail, true),
  num("coordinatorCount", "KC's", "Contact", (r) => r.coordinatorCount),
  num("programmeTotal", "Programma's", "Programma", (r) => r.programmeTotal, true),
  num("vastCount", "Vast", "Programma", (r) => r.vastCount, true),
  num("keuzeCount", "Keuze", "Programma", (r) => r.keuzeCount, true),
  num("technohubCount", "TechnoHub", "Programma", (r) => r.technohubCount, true),
  num("staffTrainingCount", "Desk.bev.", "Programma", (r) => r.staffTrainingCount, true),
  num("customCount", "Maatwerk", "Programma", (r) => r.customCount, true),
  num("optOutCount", "Afmeldingen", "Programma", (r) => r.optOutCount, true),
  // available-to-add
  text("officialName", "Officiële naam", "Basis", (r) => r.officialName),
  text("shortName", "Korte naam", "Basis", (r) => r.shortName),
  text("denomination", "Denominatie", "Basis", (r) => r.denomination),
  {
    id: "active", label: "Actief", group: "Basis",
    accessor: (r) => r.active,
    render: (r) => (r.active ? "Ja" : "Nee"),
    csv: (r) => r.active,
    sortValue: (r) => (r.active ? 1 : 0),
  },
  text("address", "Bezoekadres", "Basis", (r) => r.address),
  text("schoolPhone", "School tel.", "Basis", (r) => r.schoolPhone),
  text("emergencyContactName", "Noodcontact", "Contact", (r) => r.emergencyContactName),
  text("emergencyContactPhone", "Noodnummer", "Contact", (r) => r.emergencyContactPhone),
  text("coordinatorPhone", "KC tel.", "Contact", (r) => r.coordinatorPhone),
  text("director", "Directeur", "Contact", (r) => r.director),
  text("directorEmail", "Directeur e-mail", "Contact", (r) => r.directorEmail),
  text("directorPhone", "Directeur tel.", "Contact", (r) => r.directorPhone),
  num("contactCount", "Contacten", "Contact", (r) => r.contactCount),
  num("studiedagCount", "Studiedagen", "Beschikbaarheid", (r) => r.studiedagCount),
  num("year_groep_1", "Ll. gr. 1", "Leerlingen", (r) => r.year_groep_1),
  num("year_groep_2", "Ll. gr. 2", "Leerlingen", (r) => r.year_groep_2),
  num("year_groep_3", "Ll. gr. 3", "Leerlingen", (r) => r.year_groep_3),
  num("year_groep_4", "Ll. gr. 4", "Leerlingen", (r) => r.year_groep_4),
  num("year_groep_5", "Ll. gr. 5", "Leerlingen", (r) => r.year_groep_5),
  num("year_groep_6", "Ll. gr. 6", "Leerlingen", (r) => r.year_groep_6),
  num("year_groep_7", "Ll. gr. 7", "Leerlingen", (r) => r.year_groep_7),
  num("year_groep_8", "Ll. gr. 8", "Leerlingen", (r) => r.year_groep_8),
  text("groupNames", "Groepsnamen", "Leerlingen", (r) => r.groupNames),
  text("selectedTitles", "Gekozen programma's", "Programma", (r) => r.selectedTitles),
  text("sectionOptOuts", "Sectie-afmeldingen", "Programma", (r) => r.sectionOptOuts),
];

export const schoolsDescriptor: DatasetDescriptor<SchoolOverzichtRow> = {
  key: "schools",
  slug: "scholen",
  resource: "scholen",
  label: "Scholen overzicht",
  shortLabel: "Scholen",
  description: "Eén regel per school: status, groepen, leerlingen, contact en programmakeuzes.",
  columns: schoolsColumns,
  quickFilters: [
    { id: "notSubmitted", label: "Nog niets aangeleverd", predicate: (r) => r.active && !r.submitted },
    { id: "noKcEmail", label: "Zonder KC e-mail", predicate: (r) => r.submitted && !r.coordinatorEmail },
    { id: "activeOnly", label: "Alleen actieve scholen", predicate: (r) => r.active },
  ],
  searchText: (r) =>
    [r.schoolName, r.officialName, r.brin, r.coordinator, r.coordinatorEmail, r.director, r.selectedTitles]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
  getRowId: (r) => String(r.schoolId),
  getRowHref: (r) => `/admin/scholen/${r.schoolId}`,
  defaultSort: { id: "schoolName", dir: "asc" },
  csvName: "scholen-overzicht",
};

// ── Contacten ──
export type ContactRow = {
  schoolId: number; schoolName: string; brin: string | null; role: string; roleCode: string;
  contactName: string; primaryEmail: string | null; allEmails: string | null; primaryPhone: string | null; allPhones: string | null;
  workdays: string | null; isPrimary: boolean; lastSubmittedAt: string | null;
};
export const contactsDescriptor: DatasetDescriptor<ContactRow> = {
  key: "contacts", slug: "contacten", resource: "scholen", label: "Contacten", shortLabel: "Contacten",
  description: "Eén regel per contactpersoon (kunstcoördinator, directeur, ...) per school.",
  columns: [
    { id: "schoolName", label: "School", group: "Basis", alwaysVisible: true, defaultVisible: true, accessor: (r) => r.schoolName, sortValue: (r) => r.schoolName },
    text("brin", "Vestigingsnr.", "Basis", (r) => r.brin, true, true),
    text("role", "Rol", "Contact", (r) => r.role, true),
    text("contactName", "Naam", "Contact", (r) => r.contactName, true),
    text("primaryEmail", "E-mail", "Contact", (r) => r.primaryEmail, true),
    text("primaryPhone", "Telefoon", "Contact", (r) => r.primaryPhone, true),
    text("workdays", "Werkdagen", "Contact", (r) => r.workdays, true),
    text("allEmails", "Alle e-mails", "Contact", (r) => r.allEmails),
    text("allPhones", "Alle telefoons", "Contact", (r) => r.allPhones),
    {
      id: "lastSubmittedAt", label: "Laatst ingediend", group: "Basis",
      accessor: (r) => r.lastSubmittedAt, render: (r) => fmtDate(r.lastSubmittedAt), csv: (r) => csvDate(r.lastSubmittedAt), sortValue: (r) => r.lastSubmittedAt ?? "", filterValue: (r) => fmtDate(r.lastSubmittedAt),
    },
  ],
  quickFilters: [
    { id: "kc", label: "Kunstcoördinatoren", predicate: (r) => r.roleCode === "cultuurcoordinator" || r.roleCode === "kunstcoordinator" },
    { id: "dir", label: "Directeuren", predicate: (r) => r.roleCode === "directeur" },
    { id: "nood", label: "Noodcontacten", predicate: (r) => r.roleCode === "__emergency_contact" },
    { id: "noEmail", label: "Zonder e-mail", predicate: (r) => !r.primaryEmail && !r.roleCode.startsWith("__") },
  ],
  searchText: (r) => [r.schoolName, r.contactName, r.primaryEmail, r.primaryPhone, r.role].filter(Boolean).join(" ").toLowerCase(),
  getRowId: (r) => `${r.schoolId}-${r.roleCode}-${r.contactName}-${r.primaryEmail ?? ""}`,
  getRowHref: (r) => `/admin/scholen/${r.schoolId}`,
  defaultSort: { id: "schoolName", dir: "asc" },
  csvName: "contacten",
  groupByColumn: { id: "schoolName", label: "school" },
};

// ── Groepen & leerlingen ──
export type GroupRow = {
  schoolId: number; schoolName: string; brin: string | null; groupName: string;
  yearLevels: string; studentCounts: string; studentTotal: number; coordinatorEmail: string | null; lastSubmittedAt: string | null;
  year_groep_1: number | null; year_groep_2: number | null; year_groep_3: number | null; year_groep_4: number | null;
  year_groep_5: number | null; year_groep_6: number | null; year_groep_7: number | null; year_groep_8: number | null;
};
export const groupsDescriptor: DatasetDescriptor<GroupRow> = {
  key: "groups", slug: "groepen", resource: "scholen", label: "Groepen & leerlingen", shortLabel: "Groepen",
  description: "Eén regel per groep, met leerjaren en leerlingaantallen.",
  columns: [
    { id: "schoolName", label: "School", group: "Basis", alwaysVisible: true, defaultVisible: true, accessor: (r) => r.schoolName, sortValue: (r) => r.schoolName },
    text("brin", "Vestigingsnr.", "Basis", (r) => r.brin, true, true),
    text("groupName", "Groep", "Leerlingen", (r) => r.groupName, true),
    text("yearLevels", "Leerjaren", "Leerlingen", (r) => r.yearLevels, true),
    text("studentCounts", "Leerlingen", "Leerlingen", (r) => r.studentCounts, true),
    num("studentTotal", "Totaal", "Leerlingen", (r) => r.studentTotal),
    text("coordinatorEmail", "KC e-mail", "Contact", (r) => r.coordinatorEmail),
    num("year_groep_1", "Ll. gr. 1", "Leerlingen", (r) => r.year_groep_1),
    num("year_groep_2", "Ll. gr. 2", "Leerlingen", (r) => r.year_groep_2),
    num("year_groep_3", "Ll. gr. 3", "Leerlingen", (r) => r.year_groep_3),
    num("year_groep_4", "Ll. gr. 4", "Leerlingen", (r) => r.year_groep_4),
    num("year_groep_5", "Ll. gr. 5", "Leerlingen", (r) => r.year_groep_5),
    num("year_groep_6", "Ll. gr. 6", "Leerlingen", (r) => r.year_groep_6),
    num("year_groep_7", "Ll. gr. 7", "Leerlingen", (r) => r.year_groep_7),
    num("year_groep_8", "Ll. gr. 8", "Leerlingen", (r) => r.year_groep_8),
  ],
  searchText: (r) => [r.schoolName, r.groupName].filter(Boolean).join(" ").toLowerCase(),
  getRowId: (r) => `${r.schoolId}-${r.groupName}`,
  getRowHref: (r) => `/admin/scholen/${r.schoolId}`,
  defaultSort: { id: "schoolName", dir: "asc" },
  csvName: "groepen",
  groupByColumn: { id: "schoolName", label: "school" },
};

// ── Inschrijvingen per groep ──
export type EnrollmentRow = {
  programmeTitle: string; provider: string | null; mode: string; modeCode: string;
  schoolId: number; schoolName: string; brin: string | null; groupName: string;
  yearLevels: string; studentTotal: number | null; preferredPeriod: string; addons: string;
  coordinatorEmail: string | null; notes: string | null; lastSubmittedAt: string | null;
};
export const enrollmentsDescriptor: DatasetDescriptor<EnrollmentRow> = {
  key: "enrollments", slug: "inschrijvingen", resource: "inschrijvingen", label: "Inschrijvingen per groep", shortLabel: "Inschrijvingen",
  description: "Eén regel per gekozen programma per groep, met voorkeursperiode en add-ons.",
  columns: [
    { id: "programmeTitle", label: "Programma", group: "Programma", alwaysVisible: true, defaultVisible: true, accessor: (r) => r.programmeTitle, sortValue: (r) => r.programmeTitle },
    text("provider", "Aanbieder", "Programma", (r) => r.provider, true),
    text("mode", "Type", "Programma", (r) => r.mode, true),
    text("schoolName", "School", "Basis", (r) => r.schoolName, true),
    text("groupName", "Groep", "Leerlingen", (r) => r.groupName, true),
    text("yearLevels", "Leerjaren", "Leerlingen", (r) => r.yearLevels, true),
    num("studentTotal", "Leerlingen", "Leerlingen", (r) => r.studentTotal, true),
    text("preferredPeriod", "Voorkeursperiode", "Programma", (r) => r.preferredPeriod, true),
    text("addons", "Add-ons", "Programma", (r) => r.addons || null),
    text("coordinatorEmail", "KC e-mail", "Contact", (r) => r.coordinatorEmail),
    text("notes", "Toelichting", "Programma", (r) => r.notes),
    { id: "brin", label: "Vestigingsnr.", group: "Basis", mono: true, accessor: (r) => r.brin, render: (r) => dash(r.brin), csv: (r) => r.brin ?? "", sortValue: (r) => r.brin ?? "" },
    {
      id: "lastSubmittedAt", label: "Laatst ingediend", group: "Basis",
      accessor: (r) => r.lastSubmittedAt, render: (r) => fmtDate(r.lastSubmittedAt), csv: (r) => csvDate(r.lastSubmittedAt), sortValue: (r) => r.lastSubmittedAt ?? "", filterValue: (r) => fmtDate(r.lastSubmittedAt),
    },
  ],
  quickFilters: [
    { id: "vast", label: "Vast", predicate: (r) => r.modeCode === "vast" },
    { id: "keuze", label: "Keuze", predicate: (r) => r.modeCode === "keuze" },
    { id: "technohub", label: "TechnoHub", predicate: (r) => r.modeCode === "technohub" },
    { id: "noPeriod", label: "Zonder voorkeursperiode", predicate: (r) => r.preferredPeriod === "Niet ingevuld" },
  ],
  searchText: (r) => [r.programmeTitle, r.provider, r.schoolName, r.groupName].filter(Boolean).join(" ").toLowerCase(),
  getRowId: (r) => `${r.schoolId}-${r.programmeTitle}-${r.groupName}-${r.modeCode}`,
  getRowHref: (r) => `/admin/scholen/${r.schoolId}`,
  defaultSort: { id: "programmeTitle", dir: "asc" },
  csvName: "inschrijvingen-per-groep",
  groupByColumn: { id: "programmeTitle", label: "programma" },
};

// ── Activiteiten samenvatting ──
export type ActivityRow = {
  programmeTitle: string; provider: string | null; mode: string; modeCode: string;
  schoolCount: number; groupCount: number; studentTotal: number; noPeriodCount: number; participatingSchools: string | null;
};
export const activitiesDescriptor: DatasetDescriptor<ActivityRow> = {
  key: "activities", slug: "activiteiten", resource: "inschrijvingen", label: "Activiteiten samenvatting", shortLabel: "Activiteiten",
  description: "Eén regel per programma, met hoeveel scholen/groepen/leerlingen het kozen.",
  columns: [
    { id: "programmeTitle", label: "Programma", group: "Programma", alwaysVisible: true, defaultVisible: true, accessor: (r) => r.programmeTitle, sortValue: (r) => r.programmeTitle },
    text("provider", "Aanbieder", "Programma", (r) => r.provider, true),
    text("mode", "Type", "Programma", (r) => r.mode, true),
    num("schoolCount", "Scholen", "Inschrijving", (r) => r.schoolCount, true),
    num("groupCount", "Groepen", "Inschrijving", (r) => r.groupCount, true),
    num("studentTotal", "Leerlingen", "Inschrijving", (r) => r.studentTotal, true),
    num("noPeriodCount", "Zonder periode", "Inschrijving", (r) => r.noPeriodCount),
    text("participatingSchools", "Scholen", "Inschrijving", (r) => r.participatingSchools),
  ],
  quickFilters: [
    { id: "withEnrol", label: "Met inschrijvingen", predicate: (r) => r.schoolCount > 0 },
    { id: "vast", label: "Vast", predicate: (r) => r.modeCode === "vast" },
    { id: "keuze", label: "Keuze", predicate: (r) => r.modeCode === "keuze" },
  ],
  searchText: (r) => [r.programmeTitle, r.provider].filter(Boolean).join(" ").toLowerCase(),
  getRowId: (r) => `${r.programmeTitle}-${r.modeCode}-${r.provider ?? ""}`,
  defaultSort: { id: "schoolCount", dir: "desc" },
  csvName: "activiteiten",
};

// ── Afmeldingen ──
export type OptOutRow = {
  schoolId: number; schoolName: string; brin: string | null; level: "School" | "Groep";
  groupName: string | null; type: string; typeCode: string; coordinatorEmail: string | null; lastSubmittedAt: string | null;
};
export const optoutsDescriptor: DatasetDescriptor<OptOutRow> = {
  key: "optouts", slug: "afmeldingen", resource: "inschrijvingen", label: "Afmeldingen", shortLabel: "Afmeldingen",
  description: "Eén regel per afmelding: 'doet dit jaar niet mee', per groep of school-breed.",
  columns: [
    { id: "schoolName", label: "School", group: "Basis", alwaysVisible: true, defaultVisible: true, accessor: (r) => r.schoolName, sortValue: (r) => r.schoolName },
    text("brin", "Vestigingsnr.", "Basis", (r) => r.brin, true, true),
    text("level", "Niveau", "Afmelding", (r) => r.level, true),
    text("groupName", "Groep", "Afmelding", (r) => r.groupName, true),
    text("type", "Type", "Afmelding", (r) => r.type, true),
    text("coordinatorEmail", "KC e-mail", "Contact", (r) => r.coordinatorEmail),
  ],
  quickFilters: [
    { id: "school", label: "Schoolbreed", predicate: (r) => r.level === "School" },
    { id: "group", label: "Per groep", predicate: (r) => r.level === "Groep" },
  ],
  searchText: (r) => [r.schoolName, r.type, r.groupName].filter(Boolean).join(" ").toLowerCase(),
  getRowId: (r) => `${r.schoolId}-${r.level}-${r.typeCode}-${r.groupName ?? ""}`,
  getRowHref: (r) => `/admin/scholen/${r.schoolId}`,
  defaultSort: { id: "schoolName", dir: "asc" },
  csvName: "afmeldingen",
  groupByColumn: { id: "schoolName", label: "school" },
};

// ── Beschikbaarheid ──
export type AvailabilityRow = {
  schoolId: number; schoolName: string; brin: string | null; type: string; typeCode: string;
  scope: "School" | "Groep"; groupName: string | null; title: string | null; periode: string; coordinatorEmail: string | null; lastSubmittedAt: string | null;
};
export const availabilityDescriptor: DatasetDescriptor<AvailabilityRow> = {
  key: "availability", slug: "beschikbaarheid", resource: "scholen", label: "Beschikbaarheid", shortLabel: "Beschikbaarheid",
  description: "Studiedagen, vrije dagdelen en vakdocenten per school.",
  columns: [
    { id: "schoolName", label: "School", group: "Basis", alwaysVisible: true, defaultVisible: true, accessor: (r) => r.schoolName, sortValue: (r) => r.schoolName },
    text("type", "Type", "Beschikbaarheid", (r) => r.type, true),
    text("scope", "Niveau", "Beschikbaarheid", (r) => r.scope, true),
    text("groupName", "Groep", "Beschikbaarheid", (r) => r.groupName, true),
    text("title", "Omschrijving", "Beschikbaarheid", (r) => r.title, true),
    text("periode", "Datum/periode", "Beschikbaarheid", (r) => r.periode || null, true),
    text("brin", "Vestigingsnr.", "Basis", (r) => r.brin, false, true),
    text("coordinatorEmail", "KC e-mail", "Contact", (r) => r.coordinatorEmail),
  ],
  quickFilters: [
    { id: "studiedagen", label: "Studiedagen", predicate: (r) => r.typeCode === "closure" },
  ],
  searchText: (r) => [r.schoolName, r.type, r.title, r.groupName].filter(Boolean).join(" ").toLowerCase(),
  getRowId: (r) => `${r.schoolId}-${r.typeCode}-${r.title ?? ""}-${r.groupName ?? ""}-${r.periode}`,
  getRowHref: (r) => `/admin/scholen/${r.schoolId}`,
  defaultSort: { id: "schoolName", dir: "asc" },
  csvName: "beschikbaarheid",
  groupByColumn: { id: "schoolName", label: "school" },
};

// ── Signalen ──
export type SignalRow = {
  priority: "Hoog" | "Middel" | "Laag"; priorityRank: number; signalType: string;
  schoolId: number; schoolName: string; detail: string; suggestedAction: string; contact: string | null; lastSubmittedAt: string | null;
};
const PRIORITY_VARIANT: Record<SignalRow["priority"], "error" | "warning" | "outline"> = { Hoog: "error", Middel: "warning", Laag: "outline" };
export const signalsDescriptor: DatasetDescriptor<SignalRow> = {
  key: "signals", slug: "signalen", resource: "scholen", label: "Signalen", shortLabel: "Signalen",
  description: "Actielijst: scholen/items die aandacht nodig hebben.",
  columns: [
    {
      id: "priority", label: "Prio", group: "Basis", alwaysVisible: true, defaultVisible: true,
      accessor: (r) => r.priority, render: (r) => <Badge variant={PRIORITY_VARIANT[r.priority]} size="sm">{r.priority}</Badge>,
      csv: (r) => r.priority, sortValue: (r) => r.priorityRank,
    },
    text("signalType", "Signaal", "Basis", (r) => r.signalType, true),
    { id: "schoolName", label: "School", group: "Basis", defaultVisible: true, accessor: (r) => r.schoolName, sortValue: (r) => r.schoolName },
    text("detail", "Detail", "Basis", (r) => r.detail, true),
    text("suggestedAction", "Actie", "Basis", (r) => r.suggestedAction, true),
    text("contact", "Contact", "Contact", (r) => r.contact, true),
  ],
  quickFilters: [
    { id: "hoog", label: "Hoog", predicate: (r) => r.priority === "Hoog" },
    { id: "actie", label: "Actie nodig", predicate: (r) => r.priority !== "Laag" },
  ],
  searchText: (r) => [r.schoolName, r.signalType, r.detail].filter(Boolean).join(" ").toLowerCase(),
  getRowId: (r) => `${r.schoolId}-${r.signalType}`,
  getRowHref: (r) => `/admin/scholen/${r.schoolId}`,
  defaultSort: { id: "priority", dir: "asc" },
  csvName: "signalen",
};

// ── Waarschuwingen ──
export type WarningRow = {
  schoolId: number; schoolName: string; groupName: string | null;
  mode: string; detail: string; suggestedAction: string; contact: string | null;
};
export const warningsDescriptor: DatasetDescriptor<WarningRow> = {
  key: "warnings", slug: "waarschuwingen", resource: "scholen", label: "Waarschuwingen", shortLabel: "Waarschuwingen",
  description: "Data-integriteit: groepen of secties zonder selectie en zonder afmelding (door ontbrekende enforcement bij launch).",
  columns: [
    { id: "schoolName", label: "School", group: "Basis", defaultVisible: true, accessor: (r) => r.schoolName, sortValue: (r) => r.schoolName },
    text("groupName", "Groep", "Basis", (r) => r.groupName, true),
    text("mode", "Categorie", "Basis", (r) => r.mode, true),
    text("detail", "Detail", "Basis", (r) => r.detail, true),
    text("suggestedAction", "Actie", "Basis", (r) => r.suggestedAction, true),
    text("contact", "Contact", "Contact", (r) => r.contact, true),
  ],
  quickFilters: [],
  searchText: (r) => [r.schoolName, r.groupName, r.mode, r.detail].filter(Boolean).join(" ").toLowerCase(),
  getRowId: (r) => `${r.schoolId}-${r.groupName ?? "school"}-${r.mode}`,
  getRowHref: (r) => `/admin/scholen/${r.schoolId}`,
  defaultSort: { id: "schoolName", dir: "asc" },
  csvName: "waarschuwingen",
};

// Registry of all console datasets (untyped at the boundary so the page can switch on key).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CONSOLE_DATASETS: DatasetDescriptor<any>[] = [
  schoolsDescriptor,
  enrollmentsDescriptor,
  activitiesDescriptor,
  contactsDescriptor,
  groupsDescriptor,
  availabilityDescriptor,
  optoutsDescriptor,
  signalsDescriptor,
  warningsDescriptor,
];

// ── URL <-> dataset mapping (the console is URL-driven so the nav + breadcrumb can deep-link) ──
// The schools dataset lives at the base path; the rest hang off it by Dutch slug. Numeric segments
// are school-detail ids (handled by a separate route), so they map back to the schools dataset.

export const CONSOLE_BASE = "/admin/scholen";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const datasetBySlug = new Map<string, DatasetDescriptor<any>>(CONSOLE_DATASETS.map((d) => [d.slug ?? d.key, d]));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function datasetHref(d: DatasetDescriptor<any>): string {
  return d.key === "schools" ? CONSOLE_BASE : `${CONSOLE_BASE}/${d.slug ?? d.key}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function datasetFromSlug(seg: string | undefined): DatasetDescriptor<any> {
  if (!seg) return schoolsDescriptor;
  return datasetBySlug.get(seg) ?? schoolsDescriptor;
}

/** Which dataset slug the current pathname represents (school-detail ids → the schools dataset). */
export function currentDatasetSlug(pathname: string): string {
  const m = pathname.match(/^\/admin\/scholen(?:\/([^/]+))?/);
  const seg = m?.[1];
  if (!seg || /^\d+$/.test(seg)) return "scholen";
  return seg;
}
